// src/pages/product/ProductList.tsx

import React, { useState } from "react";
// 1. EDataTable bileşenini içe aktar
import { EDataTable } from "../../../components/EDataTable";
import { TableActions } from "../../../components/TableActions";
// Ürün yönetimi için store
import { useProductStore } from "../../../store/productStore";
// PrimeReact Column sadece sütunları tanımlamak için kullanılır
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { IconButton, TextButton } from "../../../components/ButtonComponents";
import { AlertTriangle, Edit, PlusCircle, Trash2, TrendingDown, Barcode, Printer, Eye, Image as ImageIcon } from "lucide-react";
import { ImageLightbox } from "../../../components/ImageLightbox";
import { SmartNetworkImage } from "../../../components/SmartNetworkImage";
import { ProductDialog } from "./ProductDialog";
import { ProductPrintDialog } from "./ProductPrintDialog";
import { toast } from "react-toastify";
import { Product } from "../../../types/product";
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { DataTableColumn, SimpleFilter } from "../../../types"; // SimpleFilter tipini kullanacağız
import { useStockRollStore } from "../../../store/stockrollStore";
import api from "../../../service/api";

export const ProductList = () => {
    // 2. Sadece store hook'unu alıyoruz
    const store = useProductStore();
    const { createItem, updateItem, deleteItem, exportExcel, importExcel } = store; // 🌟 Excel fonksiyonlarını store'dan çektik

    const { handleApiError } = useApiErrorHandler();

    // --- STATE'LER (Sadece CRUD ve Dialog için) ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isRollsModalOpen, setIsRollsModalOpen] = useState(false);
    const [loadingRolls, setLoadingRolls] = useState(false);
    const [productToEdit, setProductToEdit] = useState<Product | null>(null);
    const [selectedProductForRolls, setSelectedProductForRolls] = useState<Product | null>(null);
    const [productRolls, setProductRolls] = useState<any[]>([]);

    // Print Dialog States
    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
    const [productToPrint, setProductToPrint] = useState<Product | null>(null);

    const apiBase = "https://api.plasiyo.com/";
    const [galleryState, setGalleryState] = useState<{
        isOpen: boolean;
        images: string[];
        currentIndex: number;
        title?: string;
        sku?: string;
    }>({
        isOpen: false,
        images: [],
        currentIndex: 0,
    });
    const rollStore = useStockRollStore();
    // useDynamicScrollHeight, first, rows, useEffect, handlePage, useTableHeader kaldırıldı.

    // --- ACTION HANDLERS (Aynı Kalır) ---
    const handleNew = () => {
        setProductToEdit(null);
        setIsModalOpen(true);
    };

    const handleEdit = (product: Product) => {
        setProductToEdit(product);
        setIsModalOpen(true);
    };

    const { confirm, modal: confirmModal } = useConfirmModal();

    const handleDeleteClick = async (product: Product) => {
        if ((product.source || "").toUpperCase() === "SYNC") {
            toast.warn("Entegrasyon (SYNC) ile aktarılan ürünler silinemez.");
            return;
        }

        const ok = await confirm(`${product.name} adlı ürünü silmek istiyor musun?`);

        if (ok) {
            try {
                // deleteItem, başarılı olursa otomatik olarak listeyi günceller (genericStore sayesinde)
                await deleteItem(product.id);
                toast.success("Ürün silindi");
            } catch (error) {
                handleApiError(error);
            }
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setProductToEdit(null);
        // NOT: Veri yenileme, CRUD metotlarının (create/update/delete) içinde yapıldığı için 
        // burada tekrar `fetchDataDynamic` çağırmaya gerek yoktur.
    };

    const handleViewRolls = async (product: Product) => {
        setSelectedProductForRolls(product);
        setIsRollsModalOpen(true);
        setLoadingRolls(true);
        try {
            const res: any = await rollStore.fetchDataDynamic({
                filter: {
                    logic: "and",
                    filters: [
                        { field: "stockId", operator: "eq", value: product.id },
                        { field: "currentQuantity", operator: "gt", value: 0 } // Sadece bakiyesi olanları göster
                    ]
                },
                pageSize: 100
            });
            const fetched = res?.items || res?.data?.items || res?.data || [];
            setProductRolls(fetched);
        } catch (error) {
            toast.error("Toplar yüklenemedi.");
        } finally {
            setLoadingRolls(false);
        }
    };

    const handleSaveProduct = async (productData: Omit<Product, "id">) => {
        const { components, ...cleanedData }: any = { ...productData };
        delete cleanedData.rolls;
        delete cleanedData.stockLocations;
        delete cleanedData.selectedCategories;
        delete cleanedData.brandName;
        delete cleanedData.secondUnitName;
        delete cleanedData.secondUnitMultiplier;

        try {
            if (productToEdit) {
                // Düzenleme
                await updateItem(productToEdit.id, cleanedData);

                // Bileşen (WMS) güncellendiyse veya temizlendiyse backend'e gönder
                if (Array.isArray(components)) {
                    await api.post(`/stock/${productToEdit.id}/components`, components);
                }

                toast.success(`Ürün Güncellendi: ${cleanedData.name}`);
            } else {
                // Oluşturma
                const newProduct: any = await createItem(cleanedData);

                // Yeni üründe de bileşen eklendiyse (WMS tarafı falan) kaydet
                if (Array.isArray(components) && components.length > 0 && newProduct && newProduct.id) {
                    await api.post(`/stock/${newProduct.id}/components`, components);
                }

                toast.success(`Yeni Ürün Eklendi: ${cleanedData.name}`);
            }
            // NOT: createItem/updateItem içindeki fetchDataDynamic listeyi zaten yeniler.

        } catch (error) {
            handleApiError(error);
        }
        handleCloseModal(); // Başarılı kayıttan sonra dialogu kapat
    };


    const handlePrintClick = (product: Product) => {
        setProductToPrint(product);
        setIsPrintModalOpen(true);
    };

    const actionBodyTemplate = (product: Product) => {
        const isSync = (product.source || "").toUpperCase() === "SYNC";

        return (
            <TableActions
                actions={[
                    {
                        icon: Printer,
                        colorClass: "text-green-600 hover:text-green-800",
                        title: "Etiket Yazdır",
                        onClick: () => handlePrintClick(product)
                    },
                    {
                        icon: Barcode,
                        colorClass: "text-purple-600 hover:text-purple-800",
                        title: "Mevcut Topları Görüntüle",
                        onClick: () => handleViewRolls(product)
                    },
                    {
                        icon: Edit,
                        colorClass: "text-blue-600 hover:text-blue-800",
                        title: "Düzenle",
                        onClick: () => handleEdit(product)
                    },
                    {
                        icon: Trash2,
                        colorClass: isSync ? "text-gray-300 dark:text-gray-600" : "text-red-500 hover:text-red-700",
                        title: isSync ? "Entegrasyon (SYNC) ile aktarılan ürünler silinemez" : "Sil",
                        disabled: isSync,
                        onClick: () => handleDeleteClick(product)
                    }
                ]}
            />
        );
    };
    const quantityBodyTemplate = (row: Product) => {
        const isCritical = row.quantity <= (row.criticalStock || 0);
        const isOutOfStock = row.quantity <= 0;

        return (
            <div className="flex flex-col gap-1">
                <div className={`flex items-center gap-2 font-bold ${isOutOfStock ? "text-red-600" : isCritical ? "text-amber-600" : " "
                    }`}>
                    <span>{row.quantity} {row.unit?.code || ''}</span>
                    {isCritical && (
                        <div className="group relative">
                            <AlertTriangle size={14} className={isOutOfStock ? "animate-pulse" : ""} />
                            {/* Basit Tooltip (Opsiyonel) */}
                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-32 bg-slate-800 text-white text-[10px] p-1.5 rounded text-center shadow-xl z-50">
                                Kritik Seviye: {row.criticalStock}
                            </span>
                        </div>
                    )}
                </div>
                {isCritical && !isOutOfStock && (
                    <span className="text-[9px] font-bold text-amber-500 uppercase tracking-tighter flex items-center gap-0.5">
                        <TrendingDown size={10} /> Stok Azalıyor
                    </span>
                )}
            </div>
        );
    };



    const productColumns: DataTableColumn<Product>[] = [
        {
            field: "images",
            header: "Görsel",
            width: "80px",
            align: "center",
            body: (row: any) => {
                const imageUrls = (row.images || [])
                    .filter((img: any) => img?.url && typeof img.url === "string" && img.url.trim().length > 0)
                    .map((img: any) => `${img.url}`);
                const hasImages = imageUrls.length > 0;

                return (
                    <div
                        onClick={() => {
                            if (hasImages) {
                                setGalleryState({
                                    isOpen: true,
                                    images: imageUrls,
                                    currentIndex: 0,
                                    title: row.name,
                                    sku: row.barcode || row.sku,
                                });
                            }
                        }}
                        className={`relative group w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center w-full border border-borderColor bg-card shadow-xs transition-all duration-200 ${hasImages ? "cursor-pointer hover:border-primary hover:shadow-md hover:scale-105" : "opacity-50"
                            }`}

                        title={hasImages ? "Görselleri incelemek için tıklayın" : "Görsel yok"}
                    >
                        <SmartNetworkImage
                            imageUrls={imageUrls}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        {hasImages && (
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white backdrop-blur-[2px]">
                                <Eye size={16} className="transform group-hover:scale-110 transition-transform drop-shadow" />
                            </div>
                        )}
                        {imageUrls.length > 1 && (
                            <div className="absolute bottom-0.5 right-0.5 px-1 py-0.2 rounded bg-black/75 text-white text-[8px] font-black tracking-tighter flex items-center gap-0.5">
                                <ImageIcon size={8} /> {imageUrls.length}
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            field: "barcode",
            header: "Barkod / SKU",
            width: "200px",
            sortable: true,
            body: (row) => <span className="font-mono text-xs text-blue-600 font-bold bg-blue-50 px-2 py-0.5 rounded border border-blue-100">{row.barcode || row.sku || '---'}</span>
        },
        { field: "name", header: "Ürün Adı", sortable: true, width: "400px" },
        {
            field: "quantity",
            header: "Miktar",
            width: "150px",
            body: quantityBodyTemplate, // Kritik stok uyarılı template
            sortable: true
        },

        {
            field: "taxRate",
            header: "KDV",
            width: "120px",
            body: (row) => (
                <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-[11px] font-bold">
                    %{row.taxRate}
                </span>
            )
        },
        {
            field: "actions",
            header: "İşlemler",
            width: "200px",
            align: "center",
            body: actionBodyTemplate
        },
    ];

    return (
        <div className="h-full flex flex-col">
            {confirmModal}

            <EDataTable<Product>
                tableKey="product_list"
                useStore={() => store}
                dataKey="id"
                title="Stok ve Ürün Yönetimi"
                placeholder="Ürün Adı/Kodu Ara..."
                searchFields={[
                    { field: "name", operator: "contains" },
                    { field: "barcode", operator: "contains" },
                    { field: "sku", operator: "contains" },
                ]}
                actions={
                    <TextButton onClick={handleNew} className="btn-new">
                        Yeni Ürün Ekle
                    </TextButton>

                }
                columns={productColumns}

                excelModule={{
                    onExport: exportExcel,
                    onImport: importExcel
                }}
            />

            <ProductDialog
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                product={
                    productToEdit
                        ? {
                            ...productToEdit,
                            variants: productToEdit.variants || [],
                            images: productToEdit.images || [],
                            components: productToEdit.components || [],
                        }
                        : null
                }
                onSave={handleSaveProduct}
            />

            <ProductPrintDialog
                isOpen={isPrintModalOpen}
                onClose={() => { setIsPrintModalOpen(false); setProductToPrint(null); }}
                product={productToPrint}
            />

            {/* Top/Barkod Listesi Dialogu */}
            <Dialog
                visible={isRollsModalOpen}
                onHide={() => { setIsRollsModalOpen(false); setSelectedProductForRolls(null); }}
                header={
                    <div className="flex items-center gap-2">
                        <Barcode size={20} className="text-[var(--primary)]" />
                        <span className="font-bold text-[var(--text)]">{selectedProductForRolls?.name} - Mevcut Toplar</span>
                    </div>
                }
                className="w-full sm:w-[600px] md:w-[800px]"
            >
                <div className="border border-[var(--border)] rounded-xl overflow-hidden bg-[var(--card)] mt-2">
                    <DataTable value={productRolls} loading={loadingRolls} className="p-datatable-sm" emptyMessage={<div className="p-4 text-center opacity-60">Bu ürüne ait mevcut top bulunmamaktadır.</div>}>
                        <Column field="barcode" header="Barkod / Top No" body={(row) => <span className="font-semibold">{row.barcode}</span>} />
                        <Column field="warehouse.name" header="Depo" body={(row) => row.warehouse?.name || '-'} />
                        <Column field="originalQuantity" header="İlk Metraj" body={(row) => <span className="opacity-60">{row.originalQuantity ?? row.quantity ?? '-'}</span>} />
                        <Column field="currentQuantity" header="Mevcut Miktar" body={(row) => (
                            <span className="font-black text-[var(--primary)] text-lg">
                                {row.currentQuantity ?? row.quantity}
                            </span>
                        )} />
                    </DataTable>
                </div>
            </Dialog>

            {/* Ürün Görsel Galerisi */}
            <ImageLightbox
                isOpen={galleryState.isOpen}
                images={galleryState.images}
                currentIndex={galleryState.currentIndex}
                onIndexChange={(idx) => setGalleryState(prev => ({ ...prev, currentIndex: idx }))}
                onClose={() => setGalleryState({ isOpen: false, images: [], currentIndex: 0, title: '', sku: '' })}
                title={galleryState.title}
                sku={galleryState.sku}
            />
        </div>
    );
};